import { useState } from "react"
import { IconUserPlus, IconTrash, IconMail, IconDots, IconUserCog, IconArrowsExchange, IconUsers } from "@tabler/icons-react"
import { RiAdminLine } from "react-icons/ri"
import { useAuth } from "@/hooks/useAuth"
import {
  useTeamMembers,
  useInvitations,
  useCreateInvitation,
  useUpdateMemberRole,
  useRemoveMember,
  useCancelInvitation,
  useTransferOwnership,
} from "@/data/hooks/api"
import type { TeamMember, Invitation, TeamRole } from "@/data/types/team.types"
import { toast } from "sonner"
import { formatDate } from "@/lib/utils"

import { Button } from "@/components/ui/button"
import { DataTable, type DataTableColumn } from "@/components/ui/data-table"
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalTitle,
  ModalDescription,
} from "@/components/Modal"
import { ButtonSelect, type ButtonSelectOption } from "@/components/ButtonSelect"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { PageContainer } from "@/components/PageContainer"
import { PageHeader } from "@/components/PageHeader"
import { Chip } from "@/components/Chip"

export function TeamPage() {
  const { user, organization } = useAuth()
  const { data: membersData, isLoading: membersLoading } = useTeamMembers()
  const { data: invitationsData, isLoading: invitationsLoading } = useInvitations()
  const createInvitation = useCreateInvitation()
  const updateMemberRole = useUpdateMemberRole()
  const removeMember = useRemoveMember()
  const cancelInvitation = useCancelInvitation()
  const transferOwnership = useTransferOwnership()

  const members = membersData?.members || []
  const invitations = invitationsData?.invitations || []
  const loading = membersLoading || invitationsLoading

  // Invite dialog state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState("")
  const [inviteRole, setInviteRole] = useState<TeamRole>("VIEWER")

  // Change role dialog state
  const [roleDialogOpen, setRoleDialogOpen] = useState(false)
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null)
  const [newRole, setNewRole] = useState<TeamRole>("VIEWER")

  // Remove member dialog state
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [memberToRemove, setMemberToRemove] = useState<TeamMember | null>(null)

  // Transfer ownership dialog state
  const [transferDialogOpen, setTransferDialogOpen] = useState(false)
  const [transferTarget, setTransferTarget] = useState<TeamMember | null>(null)

  // Cancel invitation dialog state
  const [cancelInviteDialogOpen, setCancelInviteDialogOpen] = useState(false)
  const [invitationToCancel, setInvitationToCancel] = useState<Invitation | null>(null)

  // Get current user's role directly from user object
  const currentUserRole = organization?.role

  // Role options for selects
  const getRoleOptions = (): ButtonSelectOption<TeamRole>[] => {
    const options: ButtonSelectOption<TeamRole>[] = []
    if (currentUserRole === "OWNER") {
      options.push({ value: "ADMIN", label: "Admin" })
    }
    options.push({ value: "VIEWER", label: "Viewer" })
    return options
  }

  const changeRoleOptions: ButtonSelectOption<TeamRole>[] = [
    { value: "ADMIN", label: "Admin" },
    { value: "VIEWER", label: "Viewer" },
  ]


  const handleInvite = async () => {
    if (!inviteEmail) {
      toast.error("Email required", {
        description: "Please enter an email address",
      })
      return
    }

    try {
      await createInvitation.mutateAsync({
        email: inviteEmail,
        role: inviteRole,
      })
      toast.success("Invitation sent", {
        description: `Invitation sent to ${inviteEmail}`,
      })
      setInviteDialogOpen(false)
      setInviteEmail("")
      setInviteRole("VIEWER")
    } catch (error: any) {
      toast.error("Failed to send invitation", {
        description: error.message,
      })
    }
  }

  const handleChangeRole = async () => {
    if (!selectedMember) return

    try {
      await updateMemberRole.mutateAsync({
        userId: selectedMember.id,
        data: { role: newRole },
      })
      toast.success("Role updated", {
        description: `${selectedMember.first_name} ${selectedMember.last_name}'s role has been updated to ${newRole}`,
      })
      setRoleDialogOpen(false)
      setSelectedMember(null)
    } catch (error: any) {
      toast.error("Failed to update role", {
        description: error.message,
      })
    }
  }

  const handleRemoveMember = async () => {
    if (!memberToRemove) return

    try {
      await removeMember.mutateAsync(memberToRemove.id)
      toast.success("Member removed", {
        description: `${memberToRemove.first_name} ${memberToRemove.last_name} has been removed from the team`,
      })
      setRemoveDialogOpen(false)
      setMemberToRemove(null)
    } catch (error: any) {
      toast.error("Failed to remove member", {
        description: error.message,
      })
    }
  }

  const handleTransferOwnership = async () => {
    if (!transferTarget) return

    try {
      await transferOwnership.mutateAsync({ user_id: transferTarget.id })
      toast.success("Ownership transferred", {
        description: `Organization ownership has been transferred to ${transferTarget.first_name} ${transferTarget.last_name}`,
      })
      setTransferDialogOpen(false)
      setTransferTarget(null)
    } catch (error: any) {
      toast.error("Failed to transfer ownership", {
        description: error.message,
      })
    }
  }

  const handleCancelInvitation = async () => {
    if (!invitationToCancel) return

    try {
      await cancelInvitation.mutateAsync(invitationToCancel.id)
      toast.success("Invitation cancelled", {
        description: `Invitation to ${invitationToCancel.email} has been cancelled`,
      })
      setCancelInviteDialogOpen(false)
      setInvitationToCancel(null)
    } catch (error: any) {
      toast.error("Failed to cancel invitation", {
        description: error.message,
      })
    }
  }

  const formatRoleName = (role: TeamRole) => {
    return role.charAt(0) + role.slice(1).toLowerCase()
  }

  // Linear Role Pill Colors (using Chart colors from design rules)
  const getRoleBadgeStyles = (role: TeamRole) => {
    switch (role) {
      case "OWNER":
        // Indigo (Chart-01)
        return {
          backgroundColor: "rgba(101, 78, 218, 0.1)",
          color: "#654EDA",
        }
      case "ADMIN":
        // Emerald Green (Chart-04)
        return {
          backgroundColor: "rgba(46, 204, 113, 0.1)",
          color: "#2ECC71",
        }
      default:
        // Slate (Chart-10)
        return {
          backgroundColor: "rgba(52, 73, 94, 0.1)",
          color: "#34495E",
        }
    }
  }

  const canChangeRole = (member: TeamMember) => {
    if (member.id === user?.id) return false
    if (member.role === "OWNER") return false
    return currentUserRole === "OWNER"
  }

  const canRemoveMember = (member: TeamMember) => {
    if (member.id === user?.id) return false
    if (member.role === "OWNER") return false
    if (currentUserRole === "OWNER") return true
    if (currentUserRole === "ADMIN" && member.role === "VIEWER") return true
    return false
  }

  const openChangeRoleDialog = (member: TeamMember) => {
    setSelectedMember(member)
    setNewRole(member.role)
    setRoleDialogOpen(true)
  }

  const openRemoveDialog = (member: TeamMember) => {
    setMemberToRemove(member)
    setRemoveDialogOpen(true)
  }

  const openTransferDialog = (member: TeamMember) => {
    setTransferTarget(member)
    setTransferDialogOpen(true)
  }

  const openCancelInviteDialog = (invitation: Invitation) => {
    setInvitationToCancel(invitation)
    setCancelInviteDialogOpen(true)
  }


  const hasActions = (member: TeamMember) => {
    return canChangeRole(member) || canRemoveMember(member) || (currentUserRole === "OWNER" && member.role !== "OWNER")
  }

  // Column definitions for active members
  const memberColumns: DataTableColumn<TeamMember>[] = [
    {
      id: "member",
      header: "Member",
      flex: 1.5,
      cell: ({ row }) => (
        <div className="flex items-center gap-[8px] min-w-0">
          <p className="text-[13px] font-normal text-[#08090A] truncate">
            {row.original.first_name} {row.original.last_name}
            {row.original.id === user?.id && (
              <span className="ml-[6px] text-[13px] text-[#8A8F98]">(You)</span>
            )}
          </p>
        </div>
      ),
    },
    {
      id: "role",
      header: "Role",
      flex: 1,
      cell: ({ row }) => {
        const role = row.original.role
        const styles = getRoleBadgeStyles(role)
        return (
          <Chip
            label={formatRoleName(role)}
            showDot
            dotColor={styles.color}
          />
        )
      },
    },
    {
      id: "joined",
      header: "Joined",
      flex: 1,
      cell: ({ row }) => <span className="text-[#8A8F98]">{formatDate(row.original.created_at)}</span>,
    },
    {
      id: "actions",
      header: "",
      flex: 0.3,
      enableSorting: false,
      cell: ({ row }) => {
        const member = row.original
        if (!hasActions(member)) return null
        return (
          <div className="flex items-center justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center justify-center size-[28px] rounded-[4px] text-[#8A8F98] hover:text-[#08090A] hover:bg-[#EDEEF3] transition-colors cursor-pointer"
                >
                  <IconDots className="size-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[160px] p-[4px] rounded-[6px] border-[#EDEEF3] shadow-lg">
                {canChangeRole(member) && (
                  <DropdownMenuItem
                    onClick={() => openChangeRoleDialog(member)}
                    className="text-[13px] h-[32px] px-[8px] rounded-[4px] text-[#08090A] cursor-pointer"
                  >
                    <IconUserCog className="mr-[8px] size-3.5 text-[#8A8F98]" />
                    Change Role
                  </DropdownMenuItem>
                )}
                {currentUserRole === "OWNER" && member.role !== "OWNER" && (
                  <DropdownMenuItem
                    onClick={() => openTransferDialog(member)}
                    className="text-[13px] h-[32px] px-[8px] rounded-[4px] text-[#08090A] cursor-pointer"
                  >
                    <IconArrowsExchange className="mr-[8px] size-3.5 text-[#8A8F98]" />
                    Transfer Ownership
                  </DropdownMenuItem>
                )}
                {canRemoveMember(member) && (
                  <>
                    <DropdownMenuSeparator className="my-[4px] bg-[#EDEEF3]" />
                    <DropdownMenuItem
                      onClick={() => openRemoveDialog(member)}
                      className="text-[13px] h-[32px] px-[8px] rounded-[4px] text-[#E74C3C] cursor-pointer focus:text-[#E74C3C] focus:bg-[#E74C3C]/5"
                    >
                      <IconTrash className="mr-[8px] size-3.5" />
                      Remove Member
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )
      },
    },
  ]

  // Column definitions for pending invitations
  const invitationColumns: DataTableColumn<Invitation>[] = [
    {
      id: "email",
      header: "Email",
      flex: 1.5,
      cell: ({ row }) => (
        <div className="flex items-center gap-[8px] min-w-0">
          <IconMail className="size-3.5 text-[#8A8F98] shrink-0" />
          <span className="text-[13px] text-[#08090A] truncate">{row.original.email}</span>
        </div>
      ),
    },
    {
      id: "role",
      header: "Role",
      flex: 1,
      cell: ({ row }) => {
        const role = row.original.role
        const styles = getRoleBadgeStyles(role)
        return (
          <Chip
            label={formatRoleName(role)}
            showDot
            dotColor={styles.color}
          />
        )
      },
    },
    {
      id: "invited_by",
      header: "Invited By",
      flex: 1,
      cell: ({ row }) => <span className="text-[13px] text-[#08090A] truncate">{row.original.invited_by_name}</span>,
    },
    {
      id: "actions",
      header: "",
      flex: 0.3,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center justify-center size-[28px] rounded-[4px] text-[#8A8F98] hover:text-[#08090A] hover:bg-[#EDEEF3] transition-colors cursor-pointer"
              >
                <IconDots className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[160px] p-[4px] rounded-[6px] border-[#EDEEF3] shadow-lg">
              <DropdownMenuItem
                onClick={() => openCancelInviteDialog(row.original)}
                className="text-[13px] h-[32px] px-[8px] rounded-[4px] text-[#E74C3C] cursor-pointer focus:text-[#E74C3C] focus:bg-[#E74C3C]/5"
              >
                <IconTrash className="mr-[8px] size-3.5" />
                Cancel Invitation
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ]

  return (
    <>
      <PageContainer>
        <PageHeader
          title="Team"
          description="Manage your team members and their access permissions."
        />

        {/* Active Team Members Section */}
        <div className="space-y-[13px]">
          <div className="flex items-center justify-between">
            <h2 className="text-[18px] font-medium text-[#08090A] px-[4px]">Active Team Members</h2>
            <Button
              onClick={() => setInviteDialogOpen(true)}
              size="sm"
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium px-[13px] rounded-[6px]"
            >
              <IconUserPlus className="size-4" />
              Invite Member
            </Button>
          </div>
          <DataTable
            data={members}
            columns={memberColumns}
            loading={loading}
            getRowId={(row) => row.id}
            emptyMessage="No members found"
            emptyIcon={<IconUsers className="size-6 text-[#8A8F98]" />}
            pagination={{
              enabled: true,
              mode: "client",
              defaultPageSize: 10,
            }}
          />
        </div>

        {/* Pending Invitations Section */}
        {invitations.length > 0 && (
          <div className="space-y-[13px] pt-[16px]">
            <h2 className="text-[18px] font-medium text-[#08090A] px-[4px]">Pending Invitations</h2>
            <DataTable
              data={invitations}
              columns={invitationColumns}
              loading={loading}
              getRowId={(row) => row.id}
              pagination={{
                enabled: false,
                mode: "client",
              }}
            />
          </div>
        )}
      </PageContainer>

      {/* Invite Member Modal */}
      <Modal open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <ModalContent className="max-w-[480px] p-0 border-[#EDEEF3] shadow-2xl">
          <ModalHeader className="p-[24px] pb-0 space-y-[13px]">
            <ModalTitle className="text-[20px] font-medium text-[#08090A]">
              Invite Team Member
            </ModalTitle>
            <ModalDescription className="text-[13px] leading-[22px] text-[#8A8F98]">
              Send an invitation to join your organization.
            </ModalDescription>
          </ModalHeader>

          <div className="px-[24px] py-[24px] space-y-[20px]">
            <div className="space-y-[8px]">
              <Label htmlFor="invite-email" className="text-[13px] text-[#08090A]">
                Email address
              </Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="colleague@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="h-[36px] px-[13px] rounded-[6px] border-[#EDEEF3] bg-[#FFFFFF] text-[13px] text-[#08090A] placeholder:text-[#8A8F98] hover:border-[#D1D5DB] focus-visible:ring-0 focus-visible:border-[#654EDA] transition-colors shadow-none"
              />
            </div>
            <div className="space-y-[8px]">
              <Label htmlFor="invite-role" className="text-[13px] text-[#08090A]">
                Role
              </Label>
              <ButtonSelect
                value={inviteRole}
                onValueChange={(value) => setInviteRole(value as TeamRole)}
                options={getRoleOptions()}
                icon={<RiAdminLine className="size-4" />}
                header="Role"
                dropdownWidth={180}
                size="sm"
                className="justify-between"
              />
            </div>
          </div>

          <ModalFooter className="justify-end p-[24px] pt-0 gap-[8px]">
            <Button
              variant="transparent-outline"
              size="sm"
              onClick={() => setInviteDialogOpen(false)}
              disabled={createInvitation.isPending}
              className="h-[32px] font-medium border-[#EDEEF3]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleInvite}
              disabled={createInvitation.isPending}
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium"
            >
              {createInvitation.isPending ? "Sending..." : "Send Invitation"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Change Role Modal */}
      <Modal open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <ModalContent className="max-w-[480px] p-0 border-[#EDEEF3] shadow-2xl">
          <ModalHeader className="p-[24px] pb-0 space-y-[13px]">
            <ModalTitle className="text-[20px] font-medium text-[#08090A]">
              Change Role
            </ModalTitle>
            <ModalDescription className="text-[13px] leading-[22px] text-[#8A8F98]">
              Update {selectedMember?.first_name} {selectedMember?.last_name}'s role
            </ModalDescription>
          </ModalHeader>

          <div className="px-[24px] py-[24px] space-y-[20px]">
            <div className="space-y-[8px]">
              <Label htmlFor="new-role" className="text-[13px] text-[#08090A]">
                Role
              </Label>
              <ButtonSelect
                value={newRole}
                onValueChange={(value) => setNewRole(value as TeamRole)}
                options={changeRoleOptions}
                icon={<RiAdminLine className="size-4" />}
                header="Role"
                dropdownWidth={180}
                size="sm"
                className="justify-between"
              />
            </div>
          </div>

          <ModalFooter className="justify-end p-[24px] pt-0 gap-[8px]">
            <Button
              variant="transparent-outline"
              size="sm"
              onClick={() => setRoleDialogOpen(false)}
              disabled={updateMemberRole.isPending}
              className="h-[32px] font-medium border-[#EDEEF3]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleChangeRole}
              disabled={updateMemberRole.isPending}
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium"
            >
              {updateMemberRole.isPending ? "Updating..." : "Update Role"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Remove Member Modal */}
      <Modal open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <ModalContent className="max-w-[480px] p-0 border-[#EDEEF3] shadow-2xl">
          <ModalHeader className="p-[24px] pb-0 space-y-[13px]">
            <ModalTitle className="text-[20px] font-medium text-[#08090A]">
              Remove Team Member
            </ModalTitle>
            <ModalDescription className="text-[13px] leading-[22px] text-[#8A8F98]">
              Are you sure you want to remove {memberToRemove?.first_name} {memberToRemove?.last_name} from the team? This action cannot be undone.
            </ModalDescription>
          </ModalHeader>

          <ModalFooter className="justify-end p-[24px] gap-[8px]">
            <Button
              variant="transparent-outline"
              size="sm"
              onClick={() => setRemoveDialogOpen(false)}
              disabled={removeMember.isPending}
              className="h-[32px] font-medium border-[#EDEEF3]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleRemoveMember}
              disabled={removeMember.isPending}
              className="h-[32px] bg-[#E74C3C] hover:bg-[#E74C3C]/90 text-white font-medium"
            >
              {removeMember.isPending ? "Removing..." : "Remove Member"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Transfer Ownership Modal */}
      <Modal open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <ModalContent className="max-w-[480px] p-0 border-[#EDEEF3] shadow-2xl">
          <ModalHeader className="p-[24px] pb-0 space-y-[13px]">
            <ModalTitle className="text-[20px] font-medium text-[#08090A]">
              Transfer Ownership
            </ModalTitle>
            <ModalDescription className="text-[13px] leading-[22px] text-[#8A8F98]">
              Are you sure you want to transfer organization ownership to {transferTarget?.first_name} {transferTarget?.last_name}? You will be demoted to admin. This action cannot be undone.
            </ModalDescription>
          </ModalHeader>

          <ModalFooter className="justify-end p-[24px] gap-[8px]">
            <Button
              variant="transparent-outline"
              size="sm"
              onClick={() => setTransferDialogOpen(false)}
              disabled={transferOwnership.isPending}
              className="h-[32px] font-medium border-[#EDEEF3]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleTransferOwnership}
              disabled={transferOwnership.isPending}
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium"
            >
              {transferOwnership.isPending ? "Transferring..." : "Transfer Ownership"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Cancel Invitation Modal */}
      <Modal open={cancelInviteDialogOpen} onOpenChange={setCancelInviteDialogOpen}>
        <ModalContent className="max-w-[480px] p-0 border-[#EDEEF3] shadow-2xl">
          <ModalHeader className="p-[24px] pb-0 space-y-[13px]">
            <ModalTitle className="text-[20px] font-medium text-[#08090A]">
              Cancel Invitation
            </ModalTitle>
            <ModalDescription className="text-[13px] leading-[22px] text-[#8A8F98]">
              Are you sure you want to cancel the invitation to <span className="font-medium text-[#08090A] inline">{invitationToCancel?.email}</span>?
            </ModalDescription>
          </ModalHeader>

          <ModalFooter className="justify-end p-[24px] gap-[8px]">
            <Button
              variant="transparent-outline"
              size="sm"
              onClick={() => setCancelInviteDialogOpen(false)}
              disabled={cancelInvitation.isPending}
              className="h-[32px] font-medium border-[#EDEEF3]"
            >
              Keep Invitation
            </Button>
            <Button
              size="sm"
              onClick={handleCancelInvitation}
              disabled={cancelInvitation.isPending}
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium"
            >
              {cancelInvitation.isPending ? "Cancelling..." : "Cancel Invitation"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
